import { CheckCircle2, Phone, ArrowUpRight, ShieldCheck, Clock, FileCheck, Sparkles } from 'lucide-react';
import { COMPANY } from '../mock/data';

const JOBBER_REQUEST_URL =
  process.env.REACT_APP_JOBBER_REQUEST_FORM_URL ||
  'https://clienthub.getjobber.com/hubs/c5b1ae8a-7513-4ab7-8313-0d1d1932e9d8/public/requests/4996573/new';

const FORM_STEPS = [
  { icon: FileCheck, label: 'Tell us about your project' },
  { icon: Clock, label: 'Pick a time that works for you' },
  { icon: ShieldCheck, label: 'We call to confirm — no obligation' },
];

export default function JobberQuoteEmbed() {
  return (
    <section
      id="quote"
      data-testid="jobber-quote-section"
      className="py-24 lg:py-32 bg-ink-soft border-t border-white/5 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #d4a13a 0%, transparent 45%)' }}
      />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12 items-start relative">
        <div className="lg:col-span-2">
          <div className="eyebrow">Request a Quote</div>
          <h2 className="section-title">Get your free fencing quote.</h2>
          <p className="section-lede">
            Tell us about your project and we&rsquo;ll come out, measure, and hand you a detailed,
            no-obligation written estimate.
          </p>

          <div className="mt-8 space-y-4">
            {['Fast Response', 'No Obligation', '100% Free'].map((x) => (
              <div key={x} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold" />
                <span className="text-cream/90">{x}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-md border border-white/10 bg-ink-panel">
            <div className="text-cream/60 text-xs uppercase tracking-widest">Prefer to talk?</div>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="mt-1 font-display uppercase text-3xl text-gold inline-flex items-center gap-2 hover:text-gold-light transition-colors"
            >
              <Phone className="w-6 h-6" /> {COMPANY.phone}
            </a>
            <div className="mt-1 text-cream/50 text-sm">{COMPANY.hours}</div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg bg-ink-panel border border-gold/30 shadow-2xl p-8 md:p-10 relative overflow-hidden">
            {/* Ambient accent */}
            <div
              className="absolute inset-0 opacity-[0.10] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at 10% 90%, #d4a13a 0%, transparent 50%)' }}
            />

            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1">
                <Sparkles className="w-3.5 h-3.5 text-gold" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                  Powered by our secure booking system
                </span>
              </div>

              <h3 className="mt-5 font-display uppercase text-3xl md:text-4xl lg:text-5xl text-cream leading-[1.02] tracking-tight">
                Start your quote in 90 seconds.
              </h3>
              <p className="mt-4 text-cream/70 text-base leading-relaxed max-w-lg">
                Our online request form drops your details straight onto our schedule and lets you
                pick a time window that works for you. A real person calls to confirm within
                business hours.
              </p>

              <ol className="mt-8 space-y-4">
                {FORM_STEPS.map((step, i) => (
                  <li key={step.label} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
                      <step.icon className="w-4.5 h-4.5 text-gold" />
                    </div>
                    <div>
                      <div className="text-gold text-[11px] font-bold uppercase tracking-[0.25em]">
                        Step {String(i + 1).padStart(2, '0')}
                      </div>
                      <div className="text-cream font-semibold mt-0.5">{step.label}</div>
                    </div>
                  </li>
                ))}
              </ol>

              <a
                href={JOBBER_REQUEST_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="jobber-start-quote-btn"
                className="mt-10 group inline-flex items-center gap-3 rounded-md bg-gold text-ink px-8 py-5 font-bold uppercase tracking-wider text-base shadow-xl hover:bg-gold-light hover:-translate-y-0.5 transition-[background-color,transform,box-shadow]"
              >
                Start My Free Quote
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <div className="mt-4 flex items-center gap-2 text-xs text-cream/50">
                <ShieldCheck className="w-3.5 h-3.5 text-gold/70" />
                <span>Secure form &middot; opens in a new tab &middot; takes ~90 seconds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
